import { useCallback, useRef, useState, useEffect } from 'react';
import { Stack, Text, Group, Box, Button, Anchor, Alert, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconClock } from '@tabler/icons-react';
import { Dropzone, type FileRejection } from '@mantine/dropzone';
import { useShallow } from 'zustand/react/shallow';
import { Icon } from '../Icon/Icon';
import { AttachmentItem } from '../AttachmentItem';
import { useChatStore } from '../../../../stores/chatStore';
import { useFileUpload } from '../../../../hooks/useFileUpload';
import { useFileDelete } from '../../../../hooks/useFileDelete';
import { isUploadApiError } from '../../../../api/base/errors';
import { trackComponentInteraction } from '../../../../utils/analytics';
import { getAllAcceptedMimeTypes } from '../../../../utils/fileTypeUtils';
import { getFilesWithUniqueNames } from '../../../../utils/fileNameUtils';
import classes from './AttachModal.module.css';
import {
  ACCEPTED_FILE_TYPE_LABEL,
  MAX_FILES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_LABEL,
  getDropzoneAccept,
  validateFileTypes,
} from '../../utils/fileUploadValidation';
import { getFileUIState } from '../../utils/fileEntryToUI';

interface AttachModalProps {
  conversationId: string; // Required for upload
  onFilesSelected?: (files: File[]) => void;
  onClose?: () => void;
  onUpload?: (uploadSessionId: string) => void; // Return upload session ID
  onUploadStateChange?: (isUploading: boolean, isDeleting: boolean) => void; // Notify parent of upload state changes
}

const ACCEPTED_FILE_TYPES = getDropzoneAccept();

// Interface for managing uploaded files staging
interface UploadedFileStaging {
  filename: string;
  staged: boolean;
}

export const AttachModal = ({ conversationId, onFilesSelected, onClose, onUpload, onUploadStateChange }: AttachModalProps) => {
  const openRef = useRef<() => void>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [deletingFiles, setDeletingFiles] = useState<string[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Local state for managing uploaded files staging
  const [uploadedFilesStaging, setUploadedFilesStaging] = useState<UploadedFileStaging[]>([]);

  // Cancel any pending auto-close timer when the component unmounts so a stale
  // closure cannot call closeAttachModal() / clearAttachments() on a new modal
  // session that the user opened after the previous upload completed.
  useEffect(() => {
    return () => {
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, []);

  // Get attachments and actions from chatStore
  const {
    attachments,
    addAttachment,
    removeAttachment,
    clearAttachments,
    closeAttachModal,
    setUploadSessionId,
    markAttachmentsAsUploaded,
    markAttachmentAsUploading,
    markAttachmentAsError,
    getStagedForUpload,
    // Conversation file management
    addConversationFile,
    markFileDeleting,
    setConversationUploadSession,
    // Pending files for message (input indicators)
    addPendingFileForMessage,
  } = useChatStore();

  // This is the single reactive source for files shown throughout the input UI.
  // Conversation query results hydrate this store in ChatInputSimple; rendering
  // query data separately here can resurrect a locally deleted file while the
  // post-delete conversation refetch is still in flight.
  const allConversationFiles = useChatStore(
    useShallow((state) => state.getConversationFiles(conversationId)),
  );

  // Initialize uploaded files staging when allConversationFiles changes
  // Preserve existing staging state to prevent user interaction loss
  useEffect(() => {
    setUploadedFilesStaging(prev => {
      // Create a map of existing staging states for quick lookup
      const existingStaging = new Map(prev.map(file => [file.filename, file.staged]));

      // Map all conversation files, preserving existing staging or defaulting to false
      return allConversationFiles.map(filename => ({
        filename,
        staged: existingStaging.get(filename) ?? false
      }));
    });
  }, [allConversationFiles]);

  // Staging management functions
  const stageUploadedFileForDeletion = useCallback((filename: string) => {
    setUploadedFilesStaging(prev =>
      prev.map(file =>
        file.filename === filename ? { ...file, staged: true } : file
      )
    );
  }, []);

  const unstageUploadedFileForDeletion = useCallback((filename: string) => {
    setUploadedFilesStaging(prev =>
      prev.map(file =>
        file.filename === filename ? { ...file, staged: false } : file
      )
    );
  }, []);

  const toggleUploadedFileStaging = useCallback((filename: string) => {
    setUploadedFilesStaging(prev =>
      prev.map(file =>
        file.filename === filename ? { ...file, staged: !file.staged } : file
      )
    );
  }, []);

  const getStagedForDeletion = useCallback((): string[] => {
    return uploadedFilesStaging
      .filter(file => file.staged)
      .map(file => file.filename);
  }, [uploadedFilesStaging]);

  const hasStagedChanges = useCallback((): boolean => {
    const hasStagedUploads = getStagedForUpload().length > 0;
    const hasStagedDeletes = getStagedForDeletion().length > 0;
    return hasStagedUploads || hasStagedDeletes;
  }, [getStagedForUpload, getStagedForDeletion]);

  // Upload hook with callbacks
  const {
    uploadFiles,
    isUploading,
    uploadError,
    reset: resetUpload
  } = useFileUpload(conversationId, {
    onUploadStart: (files, deletedFiles) => {
      // Mark all attachments as uploading
      files.forEach((_, index) => {
        if (attachments[index]) {
          markAttachmentAsUploading(attachments[index].id);
        }
      });
      // Mark files being deleted as deleting
      if (deletedFiles && deletedFiles.length > 0) {
        setDeletingFiles(prev => [...prev, ...deletedFiles]);
        deletedFiles.forEach(filename => markFileDeleting(conversationId, filename));
      }
    },
    onUploadSuccess: (response) => {

      // Keep files marked as deleting until the server emits completion.
      // ChatInputSimple then removes them from local state.

      // Store files in conversation-level state for persistence
      const uploadedFileNames = getStagedForUpload().map(attachment => attachment.file.name);

      // Track file uploads with analytics
      getStagedForUpload().forEach(attachment => {
        const fileType = attachment.file.type || attachment.file.name.split('.').pop() || 'unknown';
        trackComponentInteraction(
          'AttachModal',
          'File Upload',
          'upload',
          'complete',
          fileType
        );
      });

      // Clear attachments FIRST to prevent duplicate display
      clearAttachments();

      // Then add to conversation state
      uploadedFileNames.forEach(filename => {
        addConversationFile(conversationId, filename);
        // Add to pending files for message (input indicators)
        addPendingFileForMessage(conversationId, filename);
      });

      // Store upload session at conversation level
      setConversationUploadSession(conversationId, response.upload_session_id);

      // Store session ID (no need to mark attachments as uploaded since they're cleared)
      setUploadSessionId(response.upload_session_id);
      setUploadSuccess(true);

      // Clear staging states and deleting files after successful operation
      setUploadedFilesStaging([]);
      setDeletingFiles([]);

      // Call parent callback with session ID
      onUpload?.(response.upload_session_id);

      // Auto-close modal after brief success display.
      // Store the timer so the cleanup effect can cancel it on unmount —
      // without this, the stale closure would call closeAttachModal() /
      // clearAttachments() on a new modal session opened before the timer fires.
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
      }
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null;
        handleClose();
      }, 1500);
    },
    onUploadError: (error) => {
      console.error('Batch operation failed:', error);
      // Mark attachments as error
      attachments.forEach(attachment => {
        markAttachmentAsError(attachment.id, error.userMessage);
      });
      // Reset staging states and clear deleting files on error for retry
      setUploadedFilesStaging(prev =>
        prev.map(file => ({ ...file, staged: false }))
      );
      setDeletingFiles([]);
    },
  });

  // File deletion hook with callbacks
  const {
    deleteFiles: deleteUploadedFiles,
    isDeleting,
    deleteError,
    reset: resetDelete
  } = useFileDelete(conversationId, {
    onDeleteStart: (filenames) => {
      setDeletingFiles(prev => [...prev, ...filenames]);
      filenames.forEach(filename => markFileDeleting(conversationId, filename));
    },
    onDeleteSuccess: () => {
      // The request was accepted, but background deletion is still running.
      // Keep the local file entry until server completion is received.
      setDeletingFiles([]);

      // Trigger re-fetch of conversation data to update uploadedFiles prop
      onUpload?.(''); // Notify parent
    },
    onDeleteError: (error) => {
      console.error('Failed to delete uploaded files:', error);
      // Clear deleting state on error
      setDeletingFiles([]);
    },
  });

  // Notify parent component of upload state changes
  useEffect(() => {
    onUploadStateChange?.(isUploading, isDeleting);
  }, [isUploading, isDeleting, onUploadStateChange]);

  const handleFilesDrop = useCallback((files: File[]) => {
    // Clear any previous validation errors
    setValidationError(null);

    // First validation: Check per-upload limit
    if (files.length > MAX_FILES) {
      setValidationError(`Cannot upload ${files.length} files at once. Maximum allowed: ${MAX_FILES} files per upload.`);
      return;
    }

    // Second validation: Check conversation total limit
    const totalExistingFiles = allConversationFiles.length + attachments.length;
    if (totalExistingFiles + files.length > MAX_FILES) {
      const errorMessage = `Cannot upload ${files.length} file(s). This conversation already has ${totalExistingFiles} file(s). Maximum allowed: ${MAX_FILES} files per conversation.`;
      setValidationError(errorMessage);
      return;
    }

    // Validate file types using shared validation
    const fileTypeValidation = validateFileTypes(files, allConversationFiles);
    if (!fileTypeValidation.valid) {
      setValidationError(fileTypeValidation.message || 'Invalid file type');
      return;
    }

    const validFiles = files.filter(file => {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        const errorMessage = `File "${file.name}" is too large (${Math.round(file.size / 1024 / 1024)}MB). Maximum size is ${MAX_FILE_SIZE_LABEL} per file.`;
        setValidationError(errorMessage);
        return false;
      }
      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        const errorMessage = `File "${file.name}" has an unsupported file type. Please upload ${ACCEPTED_FILE_TYPE_LABEL} files.`;
        setValidationError(errorMessage);
        return false;
      }
      return true;
    });

    // Only proceed if we have valid files and no errors
    if (validFiles.length > 0 && validFiles.length === files.length) {
      // Normalize file names to avoid collisions with existing conversation files or staged attachments
      const existingNames = [...allConversationFiles, ...attachments.map(a => a.file.name)];
      const filesWithUniqueNames = getFilesWithUniqueNames(validFiles, existingNames);

      onFilesSelected?.(filesWithUniqueNames);
    }
  }, [
    allConversationFiles,
    attachments,
    onFilesSelected,
    conversationId
  ]);

  const handleChooseFile = useCallback(() => {
    openRef.current?.();
  }, []);

  const handleConfirmChanges = useCallback(async () => {
    const filesToUpload = getStagedForUpload().map(a => a.file);
    const filesToDelete = getStagedForDeletion();

    if (filesToUpload.length === 0 && filesToDelete.length === 0) return;

    // Track file uploads immediately when confirm is clicked
    if (filesToUpload.length > 0) {
      filesToUpload.forEach(file => {
        trackComponentInteraction(
          'AttachModal',
          'File Upload',
          'upload',
          'confirm',
          file.type || file.name.split('.').pop() || 'unknown'
        );
      });
    }

    try {
      if (filesToUpload.length > 0 && filesToDelete.length > 0) {
        // Both upload and delete - use upload endpoint with deletedFiles
        await uploadFiles({
          files: filesToUpload,
          conversationId,
          deletedFiles: filesToDelete,
        });
      } else if (filesToUpload.length > 0) {
        // Upload only - use upload endpoint
        await uploadFiles({
          files: filesToUpload,
          conversationId,
          deletedFiles: [],
        });
      } else if (filesToDelete.length > 0) {
        // Delete only - use dedicated delete endpoint
        await deleteUploadedFiles({
          conversationId,
          filenames: filesToDelete,
        });
      }
    } catch (error) {
      console.error('Batch operation initiation failed:', error);
      // Error is handled by the respective hooks
    }
  }, [getStagedForUpload, getStagedForDeletion, conversationId, uploadFiles, deleteUploadedFiles]);

  const handleClose = useCallback(() => {
    // Prevent closing if uploads, deletions, or file processing (legacy) is in progress
    if (isUploading || isDeleting) {
      return; // Early return prevents modal from closing
    }

    // Clear attachments and reset state when closing modal
    clearAttachments();
    resetUpload();
    resetDelete();
    setUploadSuccess(false);
    setValidationError(null); // Clear validation errors
    setDeletingFiles([]);
    setUploadedFilesStaging([]); // Clear staging states

    onClose?.() || closeAttachModal();
  }, [isUploading, isDeleting, onClose, closeAttachModal, clearAttachments, resetUpload, resetDelete]);

  const handleRemoveAttachment = useCallback((id: string) => {
    removeAttachment(id);
  }, [removeAttachment]);

  const handleToggleUploadedFileStaging = useCallback((filename: string) => {
    toggleUploadedFileStaging(filename);
  }, [toggleUploadedFileStaging]);

  // Handle files rejected by Dropzone built-in validation (file size, file type)
  const handleFilesReject = useCallback((rejectedFiles: FileRejection[]) => {
    if (rejectedFiles.length === 0) return;

    console.log('Files rejected by Dropzone:', rejectedFiles);

    // Handle rejection reasons (file size, file type)
    const firstRejection = rejectedFiles[0];
    if (firstRejection.errors) {
      const error = firstRejection.errors[0];
      switch (error.code) {
        case 'file-too-large':
          setValidationError(`File "${firstRejection.file.name}" is too large (${Math.round(firstRejection.file.size / 1024 / 1024)}MB). Maximum size is ${MAX_FILE_SIZE_LABEL} per file.`);
          break;
        case 'file-invalid-type':
          setValidationError(`File "${firstRejection.file.name}" has an unsupported file type. Please upload ${ACCEPTED_FILE_TYPE_LABEL} files.`);
          break;
        default:
          setValidationError(`File "${firstRejection.file.name}" was rejected. Please check file type and size requirements.`);
      }
    }
  }, []);

  const stagedUploadCount = getStagedForUpload().length;
  const stagedDeleteCount = getStagedForDeletion().length;

  const primaryButtonLabel = uploadSuccess
    ? 'Changes Complete'
    : (isUploading || isDeleting)
      ? 'Processing...'
      : stagedUploadCount > 0
        ? 'Upload'
        : stagedDeleteCount > 0
          ? 'Confirm'
          : 'Upload';

  return (
    <Box className={classes.modal}>
      <Stack gap={36}>
        {/* Title */}
        <Text className={classes.title}>Attach</Text>

        {/* Instructions */}
        <Text className={classes.instructions}>
          Please upload files of the same type together.{'\n'}
          Different file types must be uploaded separately.
        </Text>


        {/* Validation Error */}
        {validationError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="File Upload Error"
            color="red"
            variant="light"
            onClose={() => setValidationError(null)}
            withCloseButton
          >
            {validationError}
          </Alert>
        )}

        {/* Upload Success */}
        {uploadSuccess && !isUploading && (
          <Alert
            icon={<IconCheck size={16} />}
            title="Upload Successful"
            color="green"
            variant="light"
          >
            All files have been uploaded successfully!
          </Alert>
        )}

        {/* Upload/Delete in Progress */}
        {(isUploading || isDeleting) && (
          <Alert
            icon={<IconClock size={16} />}
            title="Please Wait"
            color="blue"
            variant="light"
          >
            Please wait while file is being {isUploading ? "uploaded" : "deleted"}. Do not close this window.
          </Alert>
        )}

        {/* Upload Error */}
        {uploadError && !isUploading && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Upload Failed"
            color="red"
            variant="light"
            onClose={() => resetUpload()}
            withCloseButton
          >
            {isUploadApiError(uploadError)
              ? uploadError.userMessage
              : 'An unexpected error occurred during upload. Please try again.'
            }
            {uploadError.uploadDetails?.failedFiles && (
              <Text size="sm" mt="xs">
                Failed files: {uploadError.uploadDetails.failedFiles.join(', ')}
              </Text>
            )}
          </Alert>
        )}

        {/* File Deletion Error */}
        {deleteError && !isDeleting && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="File Deletion Failed"
            color="red"
            variant="light"
            onClose={() => resetDelete()}
            withCloseButton
          >
            {isUploadApiError(deleteError)
              ? deleteError.userMessage
              : 'Failed to delete file. Please try again.'
            }
            {deleteError.uploadDetails?.failedFiles && (
              <Text size="sm" mt="xs">
                Failed to delete: {deleteError.uploadDetails.failedFiles.join(', ')}
              </Text>
            )}
          </Alert>
        )}

        {/* Drop Zone */}
        <Dropzone
          openRef={openRef}
          onDrop={handleFilesDrop}
          onReject={handleFilesReject}
          className={`${classes.dropzone} ${attachments.length > 0 ? classes.dropzoneWithFiles : ''}`}
          accept={ACCEPTED_FILE_TYPES}
          maxSize={MAX_FILE_SIZE_BYTES}
          disabled={allConversationFiles.length >= MAX_FILES}
        >
          <Stack gap={18} align="center">
            {/* Paperclip Icon - Always visible */}
            <Icon name="attach" size={24} className={classes.uploadIcon} />

            {/* Upload Text - Conditional based on file limit */}
            {allConversationFiles.length >= MAX_FILES ? (
              <Box className={classes.uploadTextContainer}>
                <Text className={classes.uploadText} c="dimmed">
                  File limit reached ({MAX_FILES}/{MAX_FILES})
                </Text>
              </Box>
            ) : (
              <Box className={classes.uploadTextContainer}>
                <Text className={classes.uploadText}>
                  <Anchor
                    onClick={handleChooseFile}
                    className={classes.chooseFileLink}
                  >
                    Choose File
                  </Anchor>
                  <span className={classes.uploadSeparator}>, </span>
                  <span className={classes.uploadAccent}>Drag & Drop</span>
                  <span className={classes.uploadSeparator}> or </span>
                  <span className={classes.uploadAccent}>Paste from Clipboard (CTRL + V)</span>
                </Text>
              </Box>
            )}

            {/* Limits Text - Always visible */}
            <Box className={classes.limitsContainer}>
              <Text className={classes.limitsText}>
                Size Limit: {MAX_FILE_SIZE_LABEL} per file, up to {Math.max(0, MAX_FILES - allConversationFiles.length)} more files
                {allConversationFiles.length > 0 && (
                  ` (${allConversationFiles.length}/${MAX_FILES} used)`
                )}
              </Text>
              <Text className={classes.limitsText}>
                {ACCEPTED_FILE_TYPE_LABEL}
              </Text>
            </Box>

            {/* Attachment Items - SAME container, SAME styling */}
            {(allConversationFiles.length > 0 || attachments.length > 0) && (
              <Box className={classes.attachmentContainer}>
                {/* Show uploaded files first - appear as completed items */}
                {allConversationFiles.map((filename) => {
                  const staging = uploadedFilesStaging.find(f => f.filename === filename);
                  const entry = useChatStore.getState().getFileEntry?.(conversationId, filename);
                  const { uploadStatusOverride, stagingState } = getFileUIState(entry, {
                    stagedForDelete: deletingFiles.includes(filename) || !!staging?.staged,
                    defaultUploaded: true,
                  });
                  const isDeletingFromEntry = entry?.status === 'deleting';
                  return (
                    <AttachmentItem
                      key={`uploaded-${filename}`}
                      filename={filename}
                      readOnly={false}
                      canDelete={true}
                      isDeleting={deletingFiles.includes(filename) || isDeletingFromEntry}
                      stagingState={stagingState}
                      uploadStatusOverride={uploadStatusOverride}
                      onRemove={() => handleToggleUploadedFileStaging(filename)}
                    />
                  );
                })}

                {/* Show current session files - maintain existing behavior */}
                {attachments.map((attachment) => (
                  <AttachmentItem
                    key={attachment.id}
                    attachment={attachment}
                    stagingState={attachment.stagingState}
                    onRemove={handleRemoveAttachment}
                  />
                ))}
              </Box>
            )}
          </Stack>
        </Dropzone>

        {/* Information Text */}
        {/* File Type Guidance */}
        <Box className={classes.guidanceContainer}>
          <Text className={classes.guidanceTitle}>File Format Guidance</Text>

          <Group gap={18} align="flex-start">
            <Text className={classes.infoText}>
              For text based files (PDF, DOCX, PPTX), only text-based information is supported (eg. no embedded images)
            </Text>
            <Text className={classes.infoText}>
              File name shouldn't include any of the following special characters: \ / : * ? {'<'} {'>'} | % $ # @ & + =
            </Text>
          </Group>
        </Box>

        {/* Action Buttons */}
        <Group gap={18} justify="flex-end">
          <Tooltip
            label="Cannot close while file operations are in progress"
            disabled={!(isUploading || isDeleting)}
            position="top"
            withArrow
          >
            <Button
              className={classes.closeButton}
              variant="subtle"
              onClick={handleClose}
              disabled={isUploading || isDeleting}
              styles={{
                root: {
                  height: 'auto',
                  padding: '9px 24px',
                },
              }}
            >
              Close
            </Button>
          </Tooltip>
          <Tooltip
            label="Cannot confirm while file operations are in progress"
            disabled={!(isUploading || isDeleting)}
            position="top"
            withArrow
          >
            <Button
              className={classes.uploadButton}
              onClick={handleConfirmChanges}
              disabled={!hasStagedChanges() || uploadSuccess || isUploading || isDeleting}
              styles={{
                root: {
                  backgroundColor: 'var(--fonterra-border-accent)',
                  color: 'var(--fonterra-text-on-accent)',
                  border: '1px solid transparent',
                  borderRadius: '6px',
                  padding: '9px 24px',
                  fontWeight: 500,
                  lineHeight: '1.6em',
                  height: 'auto',
                  '&:hover': {
                    backgroundColor: 'var(--fonterra-accent-positive-hover)'
                  },
                  '&:disabled': {
                    backgroundColor: 'var(--fonterra-accent-positive-disabled)',
                    color: 'var(--fonterra-text-on-accent)',
                    opacity: 0.7,
                    cursor: 'not-allowed'
                  }
                }
              }}
            >
              {primaryButtonLabel}
            </Button>
          </Tooltip>
        </Group>
      </Stack>
    </Box>
  );
};

export type { AttachModalProps };
